import React from 'react';
import PropTypes from 'prop-types';

import {Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, Dropdown, DropdownMenuItem, Stack} from 'views/components';

import {Translate, translate, CaregiverAutocomplete, MarkdownEditor} from 'views/module';
import simplifyQuestionss from 'utils/user_bio/simplify_questions';

const AddCaregiver = (props) => {
  const [caregiver, setCaregiver] = React.useState(null);
  const [caregiverStatus, setCaregiverStatus] = React.useState('REVIEW');
  const [customRecommendation, setCustomRecommendation] = React.useState('');

  const {
    onClose,
    addCaregiver,
  } = props;

  const handleSetCaregiver = React.useCallback((selectedCaregiver) => {
    setCaregiver(selectedCaregiver);
  }, [caregiver]);

  const requiredBioFields = React.useMemo(() => ([
    'firstName',
    'lastName',
    'city',
    'province',
    'shortBio',
    'personalBio',
    'caregiverMotivation',
  ]), []);

  const caregiverQuestions = React.useMemo(() => {
    const caregiverBioQuestions = caregiver?.bio?.questions || {};
    return simplifyQuestionss(caregiverBioQuestions);
  }, [caregiver]);

  const missingFields = React.useMemo(() => {
    return requiredBioFields.filter(
        (field) =>
          !caregiverQuestions?.[field] ||
        (typeof caregiverQuestions[field] === 'string' && !caregiverQuestions[field].trim()),
    );
  }, [caregiverQuestions, requiredBioFields]);

  const missingFieldsSentence = React.useMemo(() => {
    return missingFields
        .map((field) =>
          translate(`caregiver.CaregiverDetail.Settings.ProfileUrl.RequiredFields.${field}`),
        )
        .join(', ');
  }, [missingFields]);

  const showMissingFieldsWarning = Boolean(caregiver?.caregiverRn && missingFields.length > 0);

  const handleAddCaregiver = () => {
    const caregiverData = {
      caregiverRn: caregiver.caregiverRn,
      status: caregiverStatus,
    };

    if (customRecommendation.trim()) {
      caregiverData.recommendation = customRecommendation.trim();
    }
    addCaregiver(caregiverData);
  };

  const CAREGIVER_STATUS = {
    Review: 'REVIEW',
    Active: 'ACTIVE',
  };

  const allStatus = translate('client.ClientDetail.CaregiverConnections.AddCaregiver.Status');

  return (
    <Dialog onClose={onClose} open={true}>
      <DialogTitle onClose={onClose}>
        <Translate expression='client.ClientDetail.CaregiverConnections.AddCaregiver.Title' />
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{my: 1}}>
          <CaregiverAutocomplete
            label={translate('client.ClientDetail.CaregiverConnections.AddCaregiver.CaregiverSearch')}
            selectedCaregiver={caregiver}
            setSelectedCaregiver={handleSetCaregiver}
          />
          {
            showMissingFieldsWarning &&
            <Alert severity="warning">
              <Translate
                expression="client.ClientDetail.CaregiverConnections.AddCaregiver.IncompleteProfile"
                substitute={{missingFields: missingFieldsSentence}}
              />
            </Alert>
          }
          <Dropdown
            onChange={setCaregiverStatus}
            value={caregiverStatus}
          >
            {
              Object.entries(allStatus).map(([k, v]) => (
                <DropdownMenuItem key={k} value={CAREGIVER_STATUS[k]}>
                  {v}
                </DropdownMenuItem>
              ))
            }
          </Dropdown>
          <MarkdownEditor
            value={customRecommendation}
            onChange={setCustomRecommendation}
            autoFocus={false}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant='outlined' onClick={onClose}>
          <Translate expression='generic.Close' />
        </Button>
        <Button
          color="primary"
          variant="contained"
          onClick={handleAddCaregiver}
        >
          <Translate expression='client.ClientDetail.CaregiverConnections.AddCaregiver.AddCaregiver' />
        </Button>
      </DialogActions>
    </Dialog>
  );
};

AddCaregiver.propTypes = {
  onClose: PropTypes.func.isRequired,
  addCaregiver: PropTypes.func.isRequired,
  clientRn: PropTypes.string.isRequired,
};

export default React.memo(AddCaregiver);
